import classNames from 'classnames';
import { getActivityStatus } from 'utils';
import {
  convertDateToTimeZone,
  DateFormatter,
  getDiffInHours,
} from 'infrastructure/functions';
import { DateFormats } from 'infrastructure/enums';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { useTranslation } from 'react-i18next';
import { isCHFReading } from 'utils/get-reading-info';
import { LATENT_READING_DIFF_HOURS } from 'infrastructure/consts';
import { type FC } from 'react';
import useUser from 'utils/useUser';

import RecoveredStatus from './recovered-status';
import s from './styles.module.scss';
import Countdown from '../countdown';
import StopSlaButton from '../stop-btn';
import CHFRenalStatus from '../chf-renal-status';
import LatentReadingStatus from './latent-reading-status';

import type { IPatientThresholds } from 'infrastructure/interfaces';
import type { Nullable } from 'infrastructure/types';
import type { TView } from '../../types';
import type {
  TRpmDashboardActivityQueryKey,
  TRpmPatientActivityQueryKey,
} from 'infrastructure/hooks/rpm';
import type {
  TMightBeErroneousReason,
  TServiceDeliveryThresholds,
  TThresholdName,
} from 'infrastructure/interfaces/i-rpm-patient-activity';
import type { ReadingStatus } from 'infrastructure/enums/reading-statuses';

interface DateColumnProps {
  globalStatus: ReadingStatus;
  idx: number;
  createdAt: string;
  timezone: string;
  view: TView;
  deviceId: string;
  readingId: string;
  getActivityQueryKey:
    | TRpmDashboardActivityQueryKey
    | TRpmPatientActivityQueryKey;
  slaDeadline?: string;
  slaReviewedTime?: string;
  serviceDeliveryThresholds?: TServiceDeliveryThresholds;
  height?: number;
  hideCommentsColumn: boolean;
  thresholdName?: Nullable<TThresholdName>;
  thresholds: IPatientThresholds;
  mightBeErroneousReason?: TMightBeErroneousReason;
  isRecovered?: boolean;
  recoveredByReadingId?: string;
  patientGuid: string;
  receivedTime?: string;
  readingTime?: string;
}

const DateColumn: FC<DateColumnProps> = ({
  globalStatus,
  idx,
  createdAt,
  timezone,
  view,
  slaDeadline,
  slaReviewedTime,
  serviceDeliveryThresholds,
  height = 240,
  readingId,
  deviceId,
  getActivityQueryKey,
  hideCommentsColumn,
  thresholdName,
  thresholds,
  mightBeErroneousReason,
  isRecovered,
  recoveredByReadingId,
  patientGuid,
  receivedTime,
  readingTime,
}) => {
  const { hasClinicalRole } = useUser();
  const { t } = useTranslation();
  const location = useLocation();
  const { slaReadings } = queryString.parse(location.search);
  const [_, { page, items }] = getActivityQueryKey;

  const status = slaReadings === 'true' ? 'slaReading' : globalStatus;
  const showCountdown =
    view === 'dashboard' &&
    slaReadings === 'true' &&
    slaDeadline &&
    serviceDeliveryThresholds;
  const currentHeight = hideCommentsColumn ? 240 : height;
  const currentPage = page ?? 1;
  const itemIndexPrefix = (currentPage - 1) * (items ?? 100);
  const isCHF = isCHFReading(thresholdName);

  const diffTime = getDiffInHours({
    biggerDate: receivedTime,
    date: readingTime,
  });

  const showLatentStatus =
    diffTime && hasClinicalRole ? diffTime >= LATENT_READING_DIFF_HOURS : false;

  return (
    <div className={s['date-cell']} style={{ height: currentHeight }}>
      <div
        className={classNames(s['date-wrapper'], {
          [s['p-t-26']]: view === 'dashboard',
          [s['p-t-24']]: hideCommentsColumn,
        })}
      >
        <div style={{ marginBottom: hideCommentsColumn ? 16 : 25 }}>
          {showLatentStatus && receivedTime && (
            <LatentReadingStatus
              receivedTime={receivedTime}
              timezone={timezone}
            />
          )}
          <p
            data-cy="reading-status"
            className={classNames(s[`status-${status}`], s.status)}
          >
            {getActivityStatus(status)}
          </p>
          {!isRecovered && thresholdName && isCHF && (
            <CHFRenalStatus
              thresholdName={thresholdName}
              thresholds={thresholds}
              mightBeErroneousReason={mightBeErroneousReason}
            />
          )}
          {isRecovered && (
            <RecoveredStatus
              recoveredByReadingId={recoveredByReadingId}
              patientGuid={patientGuid}
            />
          )}
        </div>
        <p data-cy="reading-number" className={s['item-number']}>
          #{itemIndexPrefix + idx + 1}
        </p>
        <div data-cy="reading-date" className={s.date}>
          <div>
            {DateFormatter({
              date: new Date(parseInt(createdAt)),
              format: DateFormats['MM-dd-yyyy hh:mm:ss a'],
            })}
          </div>
          <br />
          <span className={s.label}>{t('labels.patientTimezone')}</span>
          <br />
          {DateFormatter({
            date: convertDateToTimeZone(
              new Date(parseInt(createdAt)),
              timezone,
            ),
            format: DateFormats['MM-dd-yyyy hh:mm:ss a'],
          })}
          <br />
        </div>
        {showCountdown && (
          <>
            <Countdown
              slaDeadline={slaDeadline}
              slaReviewedTime={slaReviewedTime}
              serviceDeliveryThresholds={serviceDeliveryThresholds}
            />
            {!slaReviewedTime && (
              <StopSlaButton
                deviceId={deviceId}
                readingId={readingId}
                getActivityQueryKey={getActivityQueryKey}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};
export default DateColumn;
